import type { ReactNode, Ref } from 'react';
import { useMemo } from 'react';

import { Box, Typography } from '@/shared/ui';

import { Permission } from '@/entities/permissions';
import useChartOptions from '@/features/_shared/chart/hooks/options/useChartOptions';
import useForwardCurveOptions from '@/features/_shared/chart/hooks/options/useForwardCurveOptions';
import useSeasonalOptions from '@/features/_shared/chart/hooks/options/useSeasonalOptions';
import useSpotOptions from '@/features/_shared/chart/hooks/options/useSpotOptions';
import RegularChart from '@/features/_shared/chart/RegularChart';
import type {
  ChartType,
  ExtendedSeriesOptionsType
} from '@/features/_shared/chart/types/highcharts';
import { usePermission } from '@/features/_shared/user';

type BasicChartBlockType = {
  layout?: { fullWidth?: boolean; height?: number };
  loading: boolean;
  error: boolean;
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  chartType?: ChartType;
  buttonSection?: ReactNode;
  containerRef?: Ref<HTMLDivElement>;
};
const BasicChartBlock = ({
  layout,
  loading,
  error,
  chartSeries,
  chartType = 'spot',
  buttonSection,
  containerRef
}: BasicChartBlockType) => {
  const { height } = layout || {};
  const { createOptions: createSeasonalOptions } = useSeasonalOptions(height);
  const { createOptions: createForwardCurveOptions } = useForwardCurveOptions(height);
  const { createOptions: createSpotOptions } = useSpotOptions(height);

  const optionsFactory = useMemo(() => {
    switch (chartType) {
      case 'forwardSimple':
        return createForwardCurveOptions;
      case 'seasonal':
        return createSeasonalOptions;
      default:
        return createSpotOptions;
    }
  }, [chartType, createForwardCurveOptions, createSeasonalOptions, createSpotOptions]);

  const { options: chartOptions } = useChartOptions(chartSeries, optionsFactory);
  const { hasPermission } = usePermission();

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <Box
        sx={{
          flex: 1,
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          You no longer have access.
          <br />
          Please upgrade to access the full chart.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className='chart-container'
      sx={{ position: 'relative', minHeight: 400 }}
    >
      {buttonSection}
      <RegularChart
        showLegend
        chartSeries={chartSeries}
        chartType={chartType}
        error={error}
        height={height}
        loading={loading}
        options={chartOptions}
      />
    </Box>
  );
};

export default BasicChartBlock;
